import { useState, useMemo } from 'react'

import { Modal, Input, Text, Tooltip } from '@mantine/core'

import { useMutation } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { CREATE_APPLICANT_FILTER_PRESET } from 'src/graphql/applicantFilterPresets.graphql'
import { COLUMN_KEY_TO_BACKEND_ENUM } from 'src/pages/ApplicantsPage/utils/columnUtils'
import { transformSortByForPreset } from 'src/pages/ApplicantsPage/utils/presetUtils'

const FilterPresetModal = ({
  opened,
  onClose,
  filters,
  refetch,
  columnVisibility = {},
  columnOrder = [],
  sortBy = [],
  pageSize = 10,
  existingPresetNames = [],
}) => {
  const [presetName, setPresetName] = useState('')

  const [createFilterPreset, { loading: createFilterPresetLoading }] =
    useMutation(CREATE_APPLICANT_FILTER_PRESET, {
      onCompleted: (data) => {
        const newPresetId =
          data.createApplicantFilterPreset.applicantFilterPresetId
        setPresetName('') // Reset the form
        onClose(presetName, newPresetId) // Pass the new preset ID to the parent
        refetch()
        toast('Filter preset saved', 'success')
      },
      onError: (error) => {
        console.error('Error saving preset:', error?.message)
        toast('Failed to save preset', 'error')
      },
    })

  // Reset form when modal closes
  const handleClose = () => {
    setPresetName('')
    onClose()
  }

  // Check for duplicate names
  const isDuplicateName = useMemo(() => {
    if (!presetName.trim()) return false
    return existingPresetNames.some(
      (existingName) =>
        existingName.toLowerCase() === presetName.trim().toLowerCase()
    )
  }, [presetName, existingPresetNames])

  // Check if form is valid
  const isFormValid = presetName.trim().length > 0 && !isDuplicateName
  const isSubmitDisabled = !isFormValid || createFilterPresetLoading

  const saveFilterPreset = async (presetName: string) => {
    // Convert frontend column configuration to backend format
    const columns = columnOrder
      .filter((key) => key !== 'select' && columnVisibility[key]) // Only visible columns, exclude select
      .map((key, index) => {
        // Check if this is a custom field column
        if (key.startsWith('customField_')) {
          const customFieldId = key.replace('customField_', '')
          return {
            column: 'CUSTOM_FIELD',
            customFieldId: customFieldId,
            order: index + 1,
          }
        }

        // Standard column mapping
        const backendEnum = COLUMN_KEY_TO_BACKEND_ENUM[key]
        if (!backendEnum) {
          return null // Skip columns without mapping
        }

        return {
          column: backendEnum,
          order: index + 1,
        }
      })
      .filter((col) => col !== null) // Remove skipped columns

    // Transform sortBy - convert CUSTOM_FIELD_<id> to proper format
    const sortByWithOrder = transformSortByForPreset(sortBy)

    // Save the full filter structure (not the GraphQL param version)
    await createFilterPreset({
      variables: {
        input: {
          name: presetName,
          filters,
          pageSize,
          ...(columns.length > 0 && { columns }),
          ...(sortByWithOrder.length > 0 && { sortBy: sortByWithOrder }),
        },
      },
    })
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      if (isSubmitDisabled) return
      saveFilterPreset(presetName)
    }
  }

  return (
    <Modal
      opened={opened}
      withCloseButton={false}
      onClose={handleClose}
      closeOnClickOutside={true}
      closeOnEscape={true}
      yOffset={100}
      overlayProps={{
        backgroundOpacity: 0.2,
        blur: 5,
      }}
      classNames={{
        body: 'flex flex-col justify-between items-start',
      }}
    >
      <Text className="text-sm font-medium text-doubleNickel-gray-500">
        View name:
      </Text>
      <Input
        placeholder="Enter view name (max 30 chars)"
        variant="unstyled"
        className="w-full"
        onKeyDown={handleKeyDown}
        value={presetName}
        onChange={(event) => setPresetName(event.target.value)}
        maxLength={30}
      />
      <div className="mt-4 flex w-full flex-row items-center justify-end gap-2">
        <DNButton variant="outline" className="h-7" onClick={handleClose}>
          Cancel
        </DNButton>

        <Tooltip
          label={
            isDuplicateName
              ? 'A view with this name already exists'
              : presetName.trim().length === 0
              ? 'Please enter a view name'
              : null
          }
          disabled={!isSubmitDisabled}
          position="top"
          withArrow
        >
          <DNButton
            variant="primary"
            className="h-7 min-w-20"
            disabled={isSubmitDisabled}
            loading={createFilterPresetLoading}
            onClick={() => {
              saveFilterPreset(presetName)
            }}
          >
            Save view
          </DNButton>
        </Tooltip>
      </div>
    </Modal>
  )
}

export default FilterPresetModal
